import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Calculator, Plus, Trash2, RotateCcw } from "lucide-react";
import {
  LETTER_GRADES,
  GRADE_POINTS,
  calculateGpa,
  gpaBand,
  loadGpaCourses,
  newCourse,
  saveGpaCourses,
  type GpaCourse,
  type LetterGrade,
} from "../../lib/gpa";

export function GpaCalculatorPage() {
  const [courses, setCourses] = useState<GpaCourse[]>(() => loadGpaCourses());

  useEffect(() => {
    saveGpaCourses(courses);
  }, [courses]);

  const result = useMemo(() => calculateGpa(courses), [courses]);
  const band = result.gpa != null ? gpaBand(result.gpa) : null;

  function update(id: string, patch: Partial<GpaCourse>) {
    setCourses((list) => list.map((c) => (c.id === id ? { ...c, ...patch } : c)));
  }

  function addRow() {
    setCourses((list) => [...list, newCourse({ name: `Course ${list.length + 1}` })]);
  }

  function removeRow(id: string) {
    setCourses((list) => (list.length <= 1 ? list : list.filter((c) => c.id !== id)));
  }

  function resetAll() {
    setCourses([newCourse({ name: "Course 1" })]);
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div>
        <Link to="/student/study" className="text-sm text-muted-foreground hover:text-primary">
          ← Study
        </Link>
        <div className="flex items-start gap-3 mt-2">
          <div className="w-12 h-12 rounded-xl bg-secondary flex items-center justify-center shrink-0">
            <Calculator size={22} className="text-primary" />
          </div>
          <div>
            <h2 className="text-2xl font-bold">GPA calculator</h2>
            <p className="text-sm text-muted-foreground mt-0.5">
              Semester GPA on the Ethiopian university 4.0 scale. Saved on this device.
            </p>
          </div>
        </div>
      </div>

      <div className="grid sm:grid-cols-3 gap-3 sm:gap-4">
        <div className="bg-card rounded-sm border border-border p-5 sm:col-span-1">
          <p className="text-xs font-semibold uppercase text-muted-foreground">Your GPA</p>
          <p className="text-3xl sm:text-4xl font-bold mt-1 tracking-tight font-display">
            {result.gpa != null ? result.gpa.toFixed(2) : "—"}
          </p>
          {band && (
            <p className={`mt-2.5 text-xs font-semibold px-2.5 py-1 rounded-xs border inline-block ${band.className}`}>
              {band.label}
            </p>
          )}
        </div>
        <div className="bg-card rounded-sm border border-border p-5">
          <p className="text-xs font-semibold uppercase text-muted-foreground">Total credits</p>
          <p className="text-2xl sm:text-3xl font-bold mt-1 font-display">{result.totalCredits}</p>
          <p className="text-xs text-muted-foreground mt-1.5">{result.validCourses} course{result.validCourses === 1 ? "" : "s"}</p>
        </div>
        <div className="bg-card rounded-sm border border-border p-5">
          <p className="text-xs font-semibold uppercase text-muted-foreground">Quality points</p>
          <p className="text-2xl sm:text-3xl font-bold mt-1 font-display">{result.qualityPoints.toFixed(2)}</p>
          <p className="text-xs text-muted-foreground mt-1.5">Σ (grade × credits)</p>
        </div>
      </div>

      <div className="bg-card rounded-sm border border-border overflow-hidden">
        <div className="px-5 py-3.5 border-b border-border flex items-center justify-between gap-3">
          <p className="font-semibold text-xs sm:text-sm">Courses this semester</p>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={resetAll}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold rounded-xs border border-border hover:bg-muted"
            >
              <RotateCcw size={12} /> Reset
            </button>
            <button
              type="button"
              onClick={addRow}
              className="btn btn-primary inline-flex items-center gap-1.5 px-3 py-1 text-xs font-semibold rounded-xs"
            >
              <Plus size={12} /> Add course
            </button>
          </div>
        </div>

        <div className="divide-y divide-border">
          <div className="hidden sm:grid grid-cols-[1fr_88px_100px_40px] gap-3 px-5 py-2 text-xs font-bold uppercase tracking-wide text-muted-foreground bg-muted/40">
            <span>Course name</span>
            <span>Credits</span>
            <span>Grade</span>
            <span />
          </div>
          {courses.map((c) => (
            <div key={c.id} className="p-4 sm:px-5 sm:py-2.5 flex flex-col sm:grid sm:grid-cols-[1fr_88px_100px_40px] gap-2.5 sm:gap-3 sm:items-center">
              <div>
                <label className="sm:hidden block text-xs font-bold uppercase tracking-wide text-muted-foreground mb-1">
                  Course name
                </label>
                <input
                  value={c.name}
                  onChange={(e) => update(c.id, { name: e.target.value })}
                  placeholder="e.g. Calculus I"
                  className="w-full px-3 py-1.5 rounded-xs border border-border text-xs sm:text-sm outline-none focus:border-primary bg-input-background"
                />
              </div>
              <div className="grid grid-cols-[1fr_1fr_auto] sm:contents gap-2 items-end sm:items-center">
                <div>
                  <label className="sm:hidden block text-xs font-bold uppercase tracking-wide text-muted-foreground mb-1">
                    Credits
                  </label>
                  <input
                    type="number"
                    min={0.5}
                    max={12}
                    step={0.5}
                    value={c.credits}
                    onChange={(e) => update(c.id, { credits: Number(e.target.value) || 0 })}
                    className="w-full px-3 py-1.5 rounded-xs border border-border text-xs sm:text-sm outline-none focus:border-primary bg-input-background"
                  />
                </div>
                <div>
                  <label className="sm:hidden block text-xs font-bold uppercase tracking-wide text-muted-foreground mb-1">
                    Grade
                  </label>
                  <select
                    value={c.grade}
                    onChange={(e) => update(c.id, { grade: e.target.value as LetterGrade })}
                    className="w-full px-2 py-1.5 rounded-xs border border-border text-xs sm:text-sm bg-card outline-none focus:border-primary"
                  >
                    {LETTER_GRADES.map((g) => (
                      <option key={g} value={g}>
                        {g} ({GRADE_POINTS[g].toFixed(2)})
                      </option>
                    ))}
                  </select>
                </div>
                <div className="pb-0.5 sm:pb-0">
                  <button
                    type="button"
                    onClick={() => removeRow(c.id)}
                    disabled={courses.length <= 1}
                    className="w-8 h-8 rounded-xs hover:bg-destructive/10 text-destructive flex items-center justify-center disabled:opacity-30"
                    title="Remove"
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <details className="bg-card rounded-sm border border-border p-4 text-xs sm:text-sm">
        <summary className="font-semibold cursor-pointer">Grade point scale</summary>
        <div className="mt-2.5 grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs text-muted-foreground">
          {LETTER_GRADES.map((g) => (
            <div key={g} className="flex justify-between px-2 py-1 rounded-xs bg-muted/50 border border-border">
              <span className="font-semibold text-foreground">{g}</span>
              <span>{GRADE_POINTS[g].toFixed(2)}</span>
            </div>
          ))}
        </div>
        <p className="text-xs text-muted-foreground mt-2.5">
          GPA = total quality points ÷ total credit hours. Check your university bulletin if it uses a slightly different A− / B+ scale.
        </p>
      </details>
    </div>
  );
}
